/* keyedalike.com — the product page ticket (sections/ka-product.liquid): <ka-ticket> wraps the gallery and the
   buy column. Ported from product.html's two scripts (the variant resolver and the tooltip clamp).
   - Color radio → hidden id, SKU, the pick label, price bar, photo, thumbnail, band text, colours and texture,
     caption, ?variant= in the URL (history.replaceState) and the ladder, nudge and totals. Everything is read from
     the radio's data attributes, which Liquid prints from the variant (price = variant.price, never a finish table).
   - Quantity: clamped 1–999 for the maths on every input, written back to the field on change and blur; stepper.
   - Ladder: every tier priced from the variant with ONE rounding rule, tierPrice() (snippets/ka-tier-price.liquid
     is the same rule for the first render; change both together); the reached tier marked; the nudge, over invisible
     copies of the longest line each tier can show, so its height never changes with the quantity; the breaks
     table in the specs section (table[data-ka-breaks]) marks the same row. The count adds the locks already on the
     cart line this add would join: the same variant with exactly the key number the field will send (a blank field
     sends "", a key number of its own), because Discount Mixer counts each cart line on its own (3 + 3 of one colour
     on two key numbers gets no break). The lines come with the page (data-lines) and from /cart.js after every add;
     the count follows the key number as it is typed. The nudge's invisible lines cover every count the colour has on
     a line, so neither the quantity nor a key number changes its height. Products tagged
     hide_quantity_breaks render no ladder, no nudge, no breaks table and no data-breaks: one tier, variant.price.
   - Tier buttons (all three cells) move the quantity to their floor only when the count is outside that tier; a tier
     the cart alone fills (quantity 1 already lands above it) is aria-disabled and does nothing.
   - Key number: on change the letters are uppercased ("x204" becomes X204, like a handed-off number; nothing is
     stripped, so the line-item property is what the customer typed), then a soft hint when the character count
     (letters and digits, like 3303, X204, 25432, A1463) isn't the brand's; cleared as soon as it fits or is empty
     (checked on every keystroke, shown only on change). The number typed into Match my key arrives once
     (sessionStorage HANDOFF, written by ka-key-search.js when a result is followed, uppercased; never in the URL):
     taken and removed on load, used or not, and put in the field only when the field is empty and the number has
     the brand's length. Match my key drops it itself when it lists a new number or clears its rows.
   - The four gallery views and the colour-name tooltip clamp (scoped to this ticket's form). The key-number help
     <dialog> is snippets/ka-key-help.liquid, opened by assets/ka-help.js (shared with Match my key).
   Dawn's product-form.js posts the form and opens the cart notification; nothing here submits. No ?qty= hook. */
(() => {
  if (customElements.get('ka-ticket')) return;

  const WIDTHS = [550, 720, 900, 1100];
  const HANDOFF = 'ka-key-number'; /* Match my key's one-shot key number (assets/ka-key-search.js writes it) */
  const clampQ = (value) => Math.max(1, Math.min(999, parseInt(value, 10) || 1));
  /* the per-lock price in a break, in cents: round half up to the cent, then × quantity (mirror of ka-tier-price) */
  const tierPrice = (list, pct) => Math.floor((list * (100 - pct) + 50) / 100);
  /* dimensions in visible text get a non-breaking hyphen (U+2011); attributes stay ASCII */
  const nbh = (text) => String(text || '').replace(/(\d)-(\d)/g, '$1‑$2');
  const srcsetOf = (url) =>
    url && /[?&]width=\d+/.test(url)
      ? WIDTHS.map((w) => `${url.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ')
      : '';

  class KaTicket extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form.ticket');
      this.radios = [...this.querySelectorAll('input[data-ka-variant]')];
      this.qty = this.querySelector('[data-ka-qty]');
      if (!this.form || !this.qty) return;
      this.ready = true;

      this.format = this.dataset.moneyFormat || '${{amount}}';
      this.breaks = [[1, 0]]; /* [from count, % off]; no data-breaks (hide_quantity_breaks) leaves this one tier */
      (this.dataset.breaks || '').split(',').forEach((pair) => {
        const [min, pct] = pair.split(':').map((n) => parseInt(n, 10));
        if (min > 1 && pct >= 0) this.breaks.push([min, pct]);
      });
      this.breaks.sort((a, b) => a[0] - b[0]);

      this.idInput = this.form.querySelector('input[name="id"]');
      this.main = this.querySelector('[data-ka-main]');
      this.img = this.querySelector('.ka-main-img');
      this.thumb = this.querySelector('.ka-thumb');
      this.bandL = this.querySelector('[data-ka-band-l]');
      this.bandR = this.querySelector('[data-ka-band-r]');
      this.views = [...this.querySelectorAll('[data-ka-views] button')];
      this.capWrap = this.querySelector('[data-ka-caption]');
      this.cap = this.querySelector('[data-ka-cap]');
      this.price = this.querySelector('[data-ka-price]');
      this.sku = this.querySelector('[data-ka-sku]');
      this.pick = this.querySelector('[data-ka-pick]');
      this.tiers = [...this.querySelectorAll('[data-ka-tier]')];
      this.nudge = this.querySelector('[data-ka-nudge]');
      this.ghosts = [...this.querySelectorAll('[data-ka-nudge-ghost]')];
      this.unit = this.querySelector('[data-ka-unit]');
      this.total = this.querySelector('[data-ka-total]');
      this.add = this.querySelector('[data-ka-add]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.key = this.querySelector('[data-ka-key]');
      this.keyWarn = this.querySelector('[data-ka-keywarn]');
      /* the key number's line-item property, from the field's name: properties[Existing key number] */
      this.keyProp = ((this.key && /^properties\[(.+)\]$/.exec(this.key.name)) || [])[1] || null;
      this.lines = this.parseLines(this.dataset.lines);

      this.radios.forEach((radio) => radio.addEventListener('change', () => radio.checked && this.select(radio)));
      this.qty.addEventListener('input', () => this.calc());
      /* on change / blur the field shows the quantity actually used (0, blank or 2000 become 1 or 999) */
      const normalise = () => {
        const q = clampQ(this.qty.value);
        if (this.qty.value !== String(q)) this.qty.value = q;
        this.calc();
      };
      this.qty.addEventListener('change', normalise);
      this.qty.addEventListener('blur', normalise);
      this.querySelectorAll('[data-ka-step]').forEach((button) =>
        button.addEventListener('click', () => {
          this.qty.value = clampQ(clampQ(this.qty.value) + parseInt(button.dataset.kaStep, 10));
          this.calc();
        })
      );
      this.tiers.forEach((tier) =>
        tier.addEventListener('click', () => this.toTier(parseInt(tier.dataset.kaTier, 10)))
      );

      if (this.key) {
        /* the line an add joins follows the key number: recount as it is typed, but redraw the ladder only when the
           count changes (the nudge is a live region) */
        const recount = () => {
          if (this.variant && this.incartOf(this.variant.id) !== this.variant.incart) this.calc();
        };
        this.key.addEventListener('input', () => {
          if (this.keyWarn) this.keyCheck(false);
          recount();
        });
        this.key.addEventListener('change', () => {
          /* the letters uppercased, like a handed-off number (x204 → X204); spaces, dashes and the rest stay as typed */
          const upper = this.key.value.toUpperCase();
          if (upper !== this.key.value) this.key.value = upper;
          if (this.keyWarn) this.keyCheck(true);
          recount();
        });
      }
      this.takeKey();

      this.views.forEach((view) => view.addEventListener('click', () => this.showView(view)));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }

      const checked = this.radios.find((radio) => radio.checked) || this.radios[0];
      this.variant = this.read(checked);
      /* the browser may restore a different colour than the server rendered (back button, form restore) */
      if (checked && this.idInput && checked.dataset.kaVariant !== this.idInput.value) this.select(checked);
      else this.calc();
      this.clampTips();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.clampTips());
      this.onResize = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.clampTips());
      };
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    money(cents) {
      const fixed = (n, decimals, thousands, point) => {
        const [whole, part] = (n / 100).toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? point + part : '');
      };
      return this.format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals': return fixed(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return fixed(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fixed(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return fixed(cents, 2, "'", '.');
          default: return fixed(cents, 2, ',', '.');
        }
      });
    }

    read(radio) {
      if (!radio) return null;
      const d = radio.dataset;
      return {
        radio,
        id: d.kaVariant,
        name: radio.value,
        sku: d.sku || '',
        price: parseInt(d.price, 10) || 0,
        group: d.group,
        caption: d.caption || '',
        hex: d.hex,
        ink: d.ink,
        texture: d.texture || '', /* glitter (Cosmos), grip (Black Grip) or none: snippets/ka-color-texture */
        img: d.img || '',
        thumb: d.thumb || '',
        alt: d.alt || '',
        incart: 0, /* set by calc(): the locks on the cart line an add would join */
        available: d.available !== 'false',
      };
    }

    select(radio) {
      this.variant = this.read(radio);
      const v = this.variant;
      if (this.idInput) this.idInput.value = v.id;
      if (this.sku) this.sku.textContent = v.sku;
      if (this.pick) this.pick.textContent = v.name;
      if (this.price) this.price.textContent = this.money(v.price);
      if (this.thumb && v.thumb) {
        this.thumb.srcset = '';
        this.thumb.src = v.thumb;
      }
      this.showColor();
      this.syncBreaks();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
      this.calc();
    }

    setImage(src, alt, photo) {
      if (!this.img || !src) return;
      this.img.srcset = srcsetOf(src);
      this.img.src = src;
      this.img.alt = alt || '';
      this.img.classList.toggle('photo', !!photo);
    }

    showColor() {
      const v = this.variant;
      if (!v) return;
      this.views.forEach((view) => view.setAttribute('aria-pressed', view.dataset.view === 'color' ? 'true' : 'false'));
      this.setImage(v.img, v.alt, false);
      if (this.main) {
        this.main.style.setProperty('--code', v.hex);
        this.main.style.setProperty('--code-ink', v.ink);
        /* the band's pattern goes and comes with its colour (ka-product.css reads data-texture) */
        if (v.texture) this.main.dataset.texture = v.texture;
        else delete this.main.dataset.texture;
      }
      if (this.bandL) this.bandL.textContent = v.name;
      if (this.bandR) this.bandR.textContent = v.sku;
      if (this.cap) this.cap.textContent = v.caption;
      if (this.capWrap) this.capWrap.hidden = !v.caption;
    }

    showView(view) {
      if (view.dataset.view === 'color') {
        this.showColor();
        return;
      }
      this.views.forEach((other) => other.setAttribute('aria-pressed', other === view ? 'true' : 'false'));
      this.setImage(view.dataset.src, view.dataset.alt, view.dataset.photo === 'true');
      if (this.bandL) this.bandL.textContent = nbh(view.dataset.l);
      if (this.bandR) this.bandR.textContent = nbh(view.dataset.r);
      if (this.capWrap) this.capWrap.hidden = true; /* the finish caption belongs to the Front photo */
    }

    tierOf(count) {
      let t = 0;
      this.breaks.forEach((b, i) => {
        if (count >= b[0]) t = i;
      });
      return t;
    }

    calc() {
      const v = this.variant;
      if (!v) return;
      v.incart = this.incartOf(v.id);
      const q = clampQ(this.qty.value);
      const count = q + v.incart;
      const t = this.tierOf(count);
      const unit = tierPrice(v.price, this.breaks[t][1]);
      if (this.unit) this.unit.textContent = this.money(unit);
      if (this.total) this.total.innerHTML = `${this.money(unit * q)}<small>+ shipping</small>`;
      if (this.add) {
        this.add.textContent = v.available
          ? (q === 1 ? this.add.dataset.one : this.add.dataset.many).replace('{n}', q)
          : this.add.dataset.sold || '';
      }
      if (this.submit) this.submit.disabled = !v.available;

      this.tiers.forEach((tier) => {
        const k = parseInt(tier.dataset.kaTier, 10);
        if (!this.breaks[k]) return;
        const b = tier.querySelector('b');
        if (b) b.textContent = this.money(tierPrice(v.price, this.breaks[k][1]));
        tier.classList.toggle('on', k === t);
        tier.setAttribute('aria-pressed', k === t ? 'true' : 'false');
        if (v.incart >= this.topOf(k)) tier.setAttribute('aria-disabled', 'true');
        else tier.removeAttribute('aria-disabled');
      });

      document.querySelectorAll('table[data-ka-breaks] tbody tr[data-t]').forEach((row) => {
        const on = parseInt(row.dataset.t, 10) === t;
        row.classList.toggle('hi', on);
        if (on) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
      });

      if (this.nudge) {
        this.nudge.innerHTML = this.nudgeLine(t, count);
        /* the invisible lines under it: each tier the cart leaves in reach, at its lowest reachable count (the most
           "more"), so the nudge keeps the height of the longest line it can show and nothing under the ladder moves.
           Worked out for every count this colour has on a cart line (each key number's line, and none), the longest
           kept per tier, so typing a key number that matches a line changes the words, never the height. */
        const counts = [0, ...this.lines.filter((line) => line.v === v.id).map((line) => line.q)];
        this.ghosts.forEach((ghost, k) => {
          let html = '';
          if (this.breaks[k]) {
            counts.forEach((c) => {
              const lo = Math.max(this.breaks[k][0], 1 + c);
              const line = lo <= this.topOf(k) ? this.nudgeLine(k, lo, c) : '';
              if (line.length > html.length) html = line;
            });
          }
          ghost.innerHTML = html;
        });
      }
    }

    /* one nudge line for a count in tier k: the next break's line, or the best line in the last tier, then the cart
       line (snippets/ka-nudge-line.liquid prints the same for the first render; change both together) */
    nudgeLine(k, count, incart = this.variant.incart) {
      const v = this.variant;
      const d = this.nudge.dataset;
      const next = this.breaks[k + 1];
      let html = d.best || '';
      if (next) {
        html = (d.next || '')
          .replace('{more}', `<b>${next[0] - count} more</b>`)
          .replace('{pct}', next[1])
          .replace('{price}', `<span class="nw">(${this.money(tierPrice(v.price, next[1]))} a ${d.unit || 'lock'})</span>`);
      }
      if (incart > 0) html += (d.cart || '').replace('{n}', incart);
      return html;
    }

    /* the highest count in tier k (the last tier has none) */
    topOf(k) {
      return this.breaks[k + 1] ? this.breaks[k + 1][0] - 1 : Infinity;
    }

    /* the tier buttons move the quantity to their floor only when the count is outside that tier, and not at all when
       the cart alone fills it (quantity 1 already lands above it; the cell is aria-disabled) */
    toTier(k) {
      const v = this.variant;
      if (!v || !this.breaks[k]) return;
      const lo = this.breaks[k][0];
      const hi = this.topOf(k);
      if (v.incart >= hi) return;
      const count = clampQ(this.qty.value) + v.incart;
      if (count < lo || count > hi) {
        this.qty.value = clampQ(lo - v.incart);
        this.calc();
      }
    }

    /* the breaks table: a finish column whose prices vary by colour follows the ticket's colour */
    syncBreaks() {
      const v = this.variant;
      document.querySelectorAll('table[data-ka-breaks]').forEach((table) => {
        const head = table.querySelector(`thead th[data-group="${v.group}"][data-varies="true"]`);
        if (!head) return;
        const name = head.querySelector('[data-ka-col-name]');
        if (name) name.textContent = v.name;
        table.querySelectorAll(`tbody tr[data-t] td[data-group="${v.group}"]`).forEach((cell) => {
          const row = parseInt(cell.closest('tr').dataset.t, 10);
          if (this.breaks[row]) cell.textContent = this.money(tierPrice(v.price, this.breaks[row][1]));
        });
      });
    }

    refreshCart() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const ids = new Set(this.radios.map((radio) => radio.dataset.kaVariant));
          this.lines = (cart.items || [])
            .filter((item) => ids.has(String(item.variant_id)))
            .map((item) => ({ v: String(item.variant_id), k: this.keyOfLine(item.properties), q: item.quantity }));
          this.calc();
        })
        .catch(() => {});
    }

    /* the key number an add will send: the field as typed, letters uppercased (the change handler does it before a
       submit); null with no field, which sends no property at all */
    keyNow() {
      return this.key ? this.key.value.toUpperCase() : null;
    }

    /* a cart line's key number: the property's value ("" when it was sent blank), null when the line has none */
    keyOfLine(properties) {
      return this.keyProp && properties && Object.prototype.hasOwnProperty.call(properties, this.keyProp)
        ? String(properties[this.keyProp])
        : null;
    }

    /* the locks on the cart line an add of this variant would join: the same variant, exactly the same key number */
    incartOf(id) {
      const key = this.keyNow();
      const line = this.lines.find((l) => l.v === String(id) && l.k === key);
      return line ? line.q : 0;
    }

    /* data-lines, printed by the section: [[variant id, key number or null, quantity], ...] for this product */
    parseLines(text) {
      try {
        return (JSON.parse(text || '[]') || []).map(([v, k, q]) => ({
          v: String(v),
          k: k === null ? null : String(k),
          q: parseInt(q, 10) || 0,
        }));
      } catch (error) {
        return [];
      }
    }

    /* key number: a soft hint when the character count (letters and digits; spaces, dashes and the rest don't count)
       doesn't fit the brand (American Lock five, Master Lock four). On change the handler above has already
       uppercased the field's letters (x204 → X204); case doesn't change the count. The hint is two spans in the live
       region: for a screen reader the whole sentence (data-warn-said, "American Lock key numbers are usually five
       characters.", visually hidden), on screen the short text that fits the hint's line (data-warn, "(usually five
       characters)", aria-hidden). Empty, the region has no children, which the CSS reads (:empty). */
    keyCheck(done) {
      const want = parseInt(this.key.dataset.length, 10) || 0;
      const n = this.key.value.replace(/[^A-Za-z0-9]/g, '').length;
      if (!want || !n || n === want) this.keyWarn.textContent = '';
      else if (done) {
        const said = document.createElement('span');
        said.className = 'sr';
        said.textContent = this.key.dataset.warnSaid || this.key.dataset.warn || '';
        const shown = document.createElement('span');
        shown.setAttribute('aria-hidden', 'true');
        shown.textContent = this.key.dataset.warn || '';
        this.keyWarn.textContent = '';
        this.keyWarn.append(said, shown);
      }
    }

    /* Match my key's number, handed over once: removed here whether it is used or not, so it lands on one product
       page at most; filled in only when the field is empty and the length is this brand's (a four-character number
       never lands on an American Lock page, nor five on a Master Lock one; no brand, no prefill). Match my key hands
       over letters and digits only, uppercased (X204, A1463). */
    takeKey() {
      let number;
      try {
        number = window.sessionStorage.getItem(HANDOFF);
        window.sessionStorage.removeItem(HANDOFF);
      } catch (error) {
        return; /* storage blocked (Safari with cookies off): no prefill */
      }
      if (!number || !this.key || this.key.value.trim()) return;
      const want = parseInt(this.key.dataset.length, 10) || 0;
      if (!want || !/^[A-Z0-9]+$/.test(number) || number.length !== want) return;
      this.key.value = number;
      if (this.keyWarn) this.keyCheck(true);
    }

    /* colour-name tooltips: each is centred over its chip; a long name on the first or last chip of a wrapped row
       would hang past the ticket's border, so measure once, again when the fonts arrive, and on resize, and nudge
       those few back in (--tx). Without it every tooltip is simply centred. */
    clampTips() {
      const tips = this.form.querySelectorAll('.fin .nm');
      if (!tips.length) return;
      const box = this.form.getBoundingClientRect();
      const pad = 6; /* stay 6px inside the ticket's border */
      tips.forEach((tip) => {
        const chip = tip.parentNode.getBoundingClientRect();
        const w = tip.offsetWidth;
        const l = chip.left + chip.width / 2 - w / 2;
        const r = l + w + 2; /* +2 = the hard shadow */
        let dx = 0;
        if (l < box.left + pad) dx = box.left + pad - l;
        else if (r > box.right - pad) dx = box.right - pad - r;
        tip.style.setProperty('--tx', `${Math.round(dx)}px`); /* offsetWidth ignores the transform: idempotent */
      });
    }
  }

  customElements.define('ka-ticket', KaTicket);
})();
